// TCX import (Garmin Training Center XML, also in Strava's account export as .tcx.gz): track points with time,
// position, elevation, distance, heart rate and cadence, plus the lap totals. Same small reader as GPX.
import type { ParsedTrack, TrackSample } from './activity';
import { eachElement, MAX_GPX_BYTES, MAX_TRACK_SAMPLES, TrackFileError } from './gpx';

const tagText = (body: string, name: string): string | undefined =>
  new RegExp(`<(?:[\\w.-]+:)?${name}(?:\\s[^>]*)?>\\s*([^<]*?)\\s*</(?:[\\w.-]+:)?${name}>`, 'i').exec(body)?.[1];

const number = (s: string | undefined): number | undefined => {
  if (s === undefined || s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

export function parseTcx(text: string): ParsedTrack {
  if (text.length > MAX_GPX_BYTES) throw new TrackFileError('This TCX file is larger than 25 MB.');
  if (!/<TrainingCenterDatabase[\s>]/i.test(text)) throw new TrackFileError('This is not a TCX file.');
  let sportHint: string | undefined;
  eachElement(text, 'Activity', (head) => { sportHint = /Sport\s*=\s*["']([^"']+)["']/i.exec(head)?.[1]; return false; });
  const samples: TrackSample[] = [];
  let tooMany = false;
  eachElement(text, 'Trackpoint', (_head, b) => {
    if (samples.length >= MAX_TRACK_SAMPLES) { tooMany = true; return false; }
    const s: TrackSample = {};
    const time = tagText(b, 'Time');
    if (time && Number.isFinite(Date.parse(time))) s.time = Date.parse(time);
    const lat = number(tagText(b, 'LatitudeDegrees'));
    const lon = number(tagText(b, 'LongitudeDegrees'));
    if (lat !== undefined && lon !== undefined && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) { s.lat = lat; s.lon = lon; }
    const ele = number(tagText(b, 'AltitudeMeters'));
    if (ele !== undefined) s.ele = ele;
    const d = number(tagText(b, 'DistanceMeters'));
    if (d !== undefined) s.distanceM = d;
    const hrAt = b.indexOf('<HeartRateBpm');
    const hr = number(hrAt === -1 ? undefined : tagText(b.slice(hrAt, hrAt + 200), 'Value'));
    if (hr !== undefined && hr > 0 && hr < 260) s.hr = hr;
    const cad = number(tagText(b, 'Cadence') ?? tagText(b, 'RunCadence'));
    if (cad !== undefined) s.cadence = cad;
    samples.push(s);
  });
  if (tooMany) throw new TrackFileError(`This TCX file has more than ${MAX_TRACK_SAMPLES} points.`);
  // Lap totals: the device's own time and distance (the lap's own fields come before its <Track>).
  let durationSec = 0;
  let distanceM = 0;
  eachElement(text, 'Lap', (_head, body) => {
    const own = body.split('<Track')[0]!;
    durationSec += number(tagText(own, 'TotalTimeSeconds')) ?? 0;
    distanceM += number(tagText(own, 'DistanceMeters')) ?? 0;
  });
  if (samples.length === 0 && durationSec === 0) throw new TrackFileError('This TCX file has no track points.');
  const totals = { ...(durationSec > 0 ? { durationSec } : {}), ...(distanceM > 0 ? { distanceM } : {}) };
  return { format: 'tcx', ...(sportHint ? { sportHint } : {}), samples, ...(Object.keys(totals).length ? { totals } : {}) };
}
